import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import s from './s.module.less';
import { Form, Select, Tooltip, message, Button, Modal } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { PRIVACY_LINK } from 'constants/common';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import { FormIdentify } from 'types/form';
import LicenseImg from 'components/LicenseImg';
import { editIdentification } from 'api/newUniprofile';

interface Props {
    isEditing?: boolean;
    isReview?: boolean;
    refetch?: () => void;
    initValue?: FormIdentify;
}
const PersonalIdentification = forwardRef((props: Props, ref) => {
    const { isEditing, isReview, refetch, initValue } = props;
    const [idTypeOptions] = useGetSettingData(EnumFields.IDENTITY_TYPE);
    const [form] = Form.useForm();
    const [currentValue, setCurrentValue] = useState<FormIdentify | null>(null);
    const [fullUrls, setFullUrls] = useState<{
        fullFrontUrl?: string;
        fullBackUrl?: string;
    }>({});
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentImage, setCurrentImage] = useState<string | null>(null);

    useEffect(() => {
        if (initValue) {
            form.setFieldsValue(initValue);
            setCurrentValue(initValue);
        }
    }, [initValue]);

    const handleFullUrlChange = (name: string) => (fullUrl: string) => {
        setFullUrls((prev) => ({ ...prev, [name]: fullUrl }));
    };

    const handleViewIdClick = (imgUrl: string) => {
        if (!imgUrl) {
            return;
        }
        setCurrentImage(imgUrl);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setCurrentImage(null);
    };

    const onSubmit = async () => {
        try {
            const formValues = await form.validateFields();
            const result = await editIdentification({
                identification: formValues,
            });
            if (result.error) {
                message.error(result.error);
            } else {
                message.success('Personal identification has been updated');
                await refetch?.();
            }
        } catch (e: any) {
            if (e.errorFields) {
                //form error
                return;
            }
            const msg = e?.toString?.() || 'data error';
            message.error(msg);
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
        onValidate: () => form.validateFields(),
    }));

    const renderEditing = () => {
        return (
            <Form
                form={form}
                initialValues={{ remember: true }}
                autoComplete="off"
                layout="vertical"
                scrollToFirstError
            >
                <Form.Item noStyle>
                    <div className={s.modalWrap}>
                        <div className={s.row}>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '360px' }}
                                    label="ID type"
                                    name={['type']}
                                    rules={[
                                        {
                                            required: true,
                                            message: 'ID type is a required field',
                                        },
                                    ]}
                                >
                                    <Select
                                        fieldNames={{ label: 'content', value: 'dictKey' }}
                                        options={idTypeOptions}
                                        placeholder="Choose a government-issued ID type"
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                                const type = getFieldValue('type');
                                if (type === 'passport') {
                                    return (
                                        <div className={s.rowItem}>
                                            <Form.Item
                                                style={{ width: '334px' }}
                                                name={['frontUrl']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Copy of your government-issued ID is a required field',
                                                    },
                                                ]}
                                            >
                                                <FileUploadPrivate
                                                    onChangeFullUrl={handleFullUrlChange(
                                                        'fullFrontUrl',
                                                    )}
                                                    title={<span className={s.required}>Copy of your government-issued ID</span>}
                                                    previewMode="vertical"
                                                />
                                            </Form.Item>
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div className="flex gap-4 items-center">
                                            <div className={s.rowItem}>
                                                <Form.Item
                                                    style={{ width: '334px' }}
                                                    name={['frontUrl']}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Front of your government-issued ID is a required field',
                                                        },
                                                    ]}
                                                >
                                                    <FileUploadPrivate
                                                        onChangeFullUrl={handleFullUrlChange(
                                                            'fullFrontUrl',
                                                        )}
                                                        title={<span className={s.required}>Front of your government-issued ID</span>}
                                                        previewMode="vertical"
                                                    />
                                                </Form.Item>
                                            </div>
                                            <div className={s.rowItem}>
                                                <Form.Item
                                                    style={{ width: '334px' }}
                                                    name={['backUrl']}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Back of your government-issued ID is a required field',
                                                        },
                                                    ]}
                                                >
                                                    <FileUploadPrivate
                                                        onChangeFullUrl={handleFullUrlChange(
                                                            'fullBackUrl',
                                                        )}
                                                        title={<span className={s.required}>Back of your government-issued ID</span>}
                                                        previewMode="vertical"
                                                    />
                                                </Form.Item>
                                            </div>
                                        </div>
                                    );
                                }
                            }}
                        </Form.Item>

                    </div>
                </Form.Item>
            </Form>
        );
    };

    const renderReview = () => {
        return (
            <>
                <div className={s.displayItem}>
                    <div className={s.label}>ID type</div>
                    <div className={s.value}>
                        {
                            idTypeOptions?.find(
                                (item) => item.dictKey === currentValue?.type,
                            )?.content
                        }
                    </div>
                </div>
                <div className={s.row}>
                    <div className={s.displayItem}>
                        <div className={s.label} style={{ marginBottom: '24px' }}>Uploaded the government-issued ID</div>
                        <div className="flex gap-4">
                            <div className={s.value} style={{ textAlign: 'center' }}>
                                <LicenseImg src={currentValue?.fullFrontUrl} />
                                <Button
                                    className={s.viewId}
                                    type="link"
                                    onClick={() => {
                                        handleViewIdClick(currentValue?.fullFrontUrl || '');
                                    }}
                                >View ID
                                </Button>

                            </div>

                            {currentValue?.type !== 'passport' && (
                                <div className={s.value} style={{ textAlign: 'center' }}>
                                    <LicenseImg src={currentValue?.fullBackUrl} />
                                    <Button
                                        className={s.viewId}
                                        type="link"
                                        onClick={() => {
                                            handleViewIdClick(currentValue?.fullBackUrl || '');
                                        }}
                                    >View ID
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    };

    return (
        <>
            {!isReview ?
                <div className={s.title}>
                    Personal identification
                    <Tooltip
                        overlayClassName={s.tipCard}
                        overlayInnerStyle={{ padding: '24px', color: '#15191E' }}
                        color="#FFFFFF"
                        title={
                            <div>
                                Your government ID is securely handled for identity
                                verification in compliance with our{' '}
                                <a
                                    style={{ textDecoration: 'underline', color: '#15191E' }}
                                    target="_blank"
                                    href={PRIVACY_LINK}
                                    rel="noreferrer"
                                >
                                    Privacy Policy
                                </a>{' '}
                                and will not be shared with patients or other providers.
                            </div>
                        }
                    >
                        <QuestionCircleOutlined className={s.tooltipIcon} />
                    </Tooltip>
                </div> : null}
            <div className={s.content}>
                {isEditing ? renderEditing() : renderReview()}
            </div>
            <Modal
                open={isModalVisible}
                footer={null}
                onCancel={handleModalClose}
                width={800}
                centered
            >
                <div className={s.modalContent}>
                    {currentImage && (
                        <img
                            src={currentImage}
                            alt="ID"
                            className={s.modalImage}
                        />
                    )}
                </div>
            </Modal>
        </>);
});

export default PersonalIdentification;
